import {useEffect, useState} from 'react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {DialogBody} from '@/components/shared/DialogBody'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {SecretReferenceInput} from '@/components/shared/SecretReferenceInput'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {FormSection} from '@/components/shared/FormSection'
import {InfoBanner} from '@/components/shared/InfoBanner'
import type {SapSfEcInboundConfig} from '@/types/inbound-connector'
import {useUpdateSapSfEcConfig} from '@/hooks/use-sap-configs'

interface SapSfEcInboundConfigEditDialogProps {
    connectorId: string
    config: SapSfEcInboundConfig
    open: boolean
    onClose: () => void
}

const DATETIME_FORMAT_OPTIONS = ['V2_LITERAL', 'ISO_8601'] as const
const AUTH_TYPE_OPTIONS = ['OAUTH2_CLIENT', 'OAUTH2_SAML_BEARER'] as const

export function SapSfEcInboundConfigEditDialog({
    connectorId, config, open, onClose,
}: SapSfEcInboundConfigEditDialogProps) {
    const i18n = useI18n()
    const updateMutation = useUpdateSapSfEcConfig(connectorId)

    const [baseUrl, setBaseUrl] = useState(config.baseUrl)
    const [entitySet, setEntitySet] = useState(config.entitySet)
    const [selectFieldsCsv, setSelectFieldsCsv] = useState(config.selectFieldsCsv ?? '')
    const [expandFieldsCsv, setExpandFieldsCsv] = useState(config.expandFieldsCsv ?? '')
    const [datetimeFormat, setDatetimeFormat] = useState(config.datetimeFormat)
    const [authType, setAuthType] = useState(config.authType)
    const [tokenUrl, setTokenUrl] = useState(config.tokenUrl ?? '')
    const [clientId, setClientId] = useState(config.clientId ?? '')
    const [clientSecretRef, setClientSecretRef] = useState('')
    const [scope, setScope] = useState(config.scope ?? '')
    const [samlAssertionTemplate, setSamlAssertionTemplate] = useState(config.samlAssertionTemplate ?? '')
    const [samlIssuer, setSamlIssuer] = useState(config.samlIssuer ?? '')
    const [samlAudience, setSamlAudience] = useState(config.samlAudience ?? '')
    const [samlSubject, setSamlSubject] = useState(config.samlSubject ?? '')
    const [samlSigningKeyRef, setSamlSigningKeyRef] = useState('')
    const [samlSigningCertificateRef, setSamlSigningCertificateRef] = useState('')
    const [pageSize, setPageSize] = useState(String(config.pageSize))
    const [connectTimeoutMs, setConnectTimeoutMs] = useState(String(config.connectTimeoutMs))
    const [readTimeoutMs, setReadTimeoutMs] = useState(String(config.readTimeoutMs))
    const [maxRetries, setMaxRetries] = useState(String(config.maxRetries))

    useEffect(() => {
        if (open) {
            setBaseUrl(config.baseUrl)
            setEntitySet(config.entitySet)
            setSelectFieldsCsv(config.selectFieldsCsv ?? '')
            setExpandFieldsCsv(config.expandFieldsCsv ?? '')
            setDatetimeFormat(config.datetimeFormat)
            setAuthType(config.authType)
            setTokenUrl(config.tokenUrl ?? '')
            setClientId(config.clientId ?? '')
            setClientSecretRef('')
            setScope(config.scope ?? '')
            setSamlAssertionTemplate(config.samlAssertionTemplate ?? '')
            setSamlIssuer(config.samlIssuer ?? '')
            setSamlAudience(config.samlAudience ?? '')
            setSamlSubject(config.samlSubject ?? '')
            setSamlSigningKeyRef('')
            setSamlSigningCertificateRef('')
            setPageSize(String(config.pageSize))
            setConnectTimeoutMs(String(config.connectTimeoutMs))
            setReadTimeoutMs(String(config.readTimeoutMs))
            setMaxRetries(String(config.maxRetries))
        }
    }, [open, config])

    // WHY: switching auth type unmounts the inactive section AND clears its values
    // so a save after a switch doesn't leak values from the previous mode (spec §9).
    const handleAuthTypeChange = (next: string) => {
        if (next !== authType) {
            if (next === 'OAUTH2_CLIENT') {
                setSamlAssertionTemplate('')
                setSamlIssuer('')
                setSamlAudience('')
                setSamlSubject('')
                setSamlSigningKeyRef('')
                setSamlSigningCertificateRef('')
            }
        }
        setAuthType(next)
    }

    const handleSave = () => {
        updateMutation.mutate({
            baseUrl,
            entitySet,
            selectFieldsCsv: selectFieldsCsv || null,
            expandFieldsCsv: expandFieldsCsv || null,
            datetimeFormat,
            authType,
            tokenUrl: tokenUrl || null,
            clientId: clientId || null,
            clientSecretRef: clientSecretRef || null,
            scope: scope || null,
            samlAssertionTemplate: samlAssertionTemplate || null,
            samlIssuer: samlIssuer || null,
            samlAudience: samlAudience || null,
            samlSubject: samlSubject || null,
            samlSigningKeyRef: samlSigningKeyRef || null,
            samlSigningCertificateRef: samlSigningCertificateRef || null,
            pageSize: parseInt(pageSize, 10) || 1,
            connectTimeoutMs: parseInt(connectTimeoutMs, 10) || 5000,
            readTimeoutMs: parseInt(readTimeoutMs, 10) || 30000,
            maxRetries: parseInt(maxRetries, 10) || 0,
            attributeMappings: null,
            version: config.version,
        }, {onSuccess: onClose})
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{i18n.inboundConnectors.sapSfEc.editDialogTitle}</DialogTitle>
                    <DialogDescription>{i18n.inboundConnectors.sapSfEc.editDialogDescription}</DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <FormSection title={i18n.inboundConnectors.sectionConnection}>
                        <FormGrid columns={1}>
                            <FormField label={i18n.inboundConnectors.baseUrl} htmlFor="sap-sf-ec-baseUrl" required>
                                <Input id="sap-sf-ec-baseUrl" value={baseUrl} onChange={e => setBaseUrl(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                        <FormGrid columns={2}>
                            <FormField label={i18n.inboundConnectors.sapSfEc.entitySet} htmlFor="sap-sf-ec-entitySet" required>
                                <Input id="sap-sf-ec-entitySet" value={entitySet}
                                       onChange={e => setEntitySet(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.sapSfEc.datetimeFormat} htmlFor="sap-sf-ec-datetimeFormat" required>
                                <Select value={datetimeFormat} onValueChange={setDatetimeFormat}>
                                    <SelectTrigger id="sap-sf-ec-datetimeFormat" data-testid="select-sap-sf-ec-datetime-format">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {DATETIME_FORMAT_OPTIONS.map(o => (
                                            <SelectItem key={o} value={o}>{o}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.sapSfEc.selectFields} htmlFor="sap-sf-ec-selectFields" fullWidth>
                                <Input id="sap-sf-ec-selectFields" value={selectFieldsCsv}
                                       onChange={e => setSelectFieldsCsv(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.sapSfEc.expandFields} htmlFor="sap-sf-ec-expandFields" fullWidth>
                                <Input id="sap-sf-ec-expandFields" value={expandFieldsCsv}
                                       onChange={e => setExpandFieldsCsv(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                    </FormSection>

                    <FormSection title={i18n.inboundConnectors.sectionAuthentication}>
                        <FormGrid columns={1}>
                            <FormField label={i18n.inboundConnectors.authType} htmlFor="sap-sf-ec-authType" required>
                                <Select value={authType} onValueChange={handleAuthTypeChange}>
                                    <SelectTrigger id="sap-sf-ec-authType" data-testid="select-sap-sf-ec-auth-type">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {AUTH_TYPE_OPTIONS.map(o => (
                                            <SelectItem key={o} value={o}>{o}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                        </FormGrid>
                        {authType === 'OAUTH2_CLIENT' && (
                            <FormGrid columns={2}>
                                <FormField label={i18n.inboundConnectors.clientId} htmlFor="sap-sf-ec-clientId">
                                    <Input id="sap-sf-ec-clientId" value={clientId}
                                           onChange={e => setClientId(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.clientSecretRef} htmlFor="sap-sf-ec-clientSecretRef">
                                    <SecretReferenceInput id="sap-sf-ec-clientSecretRef"
                                                          value={clientSecretRef}
                                                          onChange={setClientSecretRef}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.tokenUrl} htmlFor="sap-sf-ec-tokenUrl">
                                    <Input id="sap-sf-ec-tokenUrl" value={tokenUrl}
                                           onChange={e => setTokenUrl(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.oauthScope} htmlFor="sap-sf-ec-scope">
                                    <Input id="sap-sf-ec-scope" value={scope}
                                           onChange={e => setScope(e.target.value)}/>
                                </FormField>
                            </FormGrid>
                        )}
                        {authType === 'OAUTH2_SAML_BEARER' && (
                            <>
                                <InfoBanner variant="info">
                                    {i18n.inboundConnectors.sapSfEc.samlBearerNote}
                                </InfoBanner>
                                <FormGrid columns={2}>
                                    <FormField label={i18n.inboundConnectors.clientId} htmlFor="sap-sf-ec-saml-clientId">
                                        <Input id="sap-sf-ec-saml-clientId" value={clientId}
                                               onChange={e => setClientId(e.target.value)}/>
                                    </FormField>
                                    <FormField label={i18n.inboundConnectors.clientSecretRef} htmlFor="sap-sf-ec-saml-clientSecretRef">
                                        <SecretReferenceInput id="sap-sf-ec-saml-clientSecretRef"
                                                              value={clientSecretRef}
                                                              onChange={setClientSecretRef}/>
                                    </FormField>
                                    <FormField label={i18n.inboundConnectors.tokenUrl} htmlFor="sap-sf-ec-saml-tokenUrl">
                                        <Input id="sap-sf-ec-saml-tokenUrl" value={tokenUrl}
                                               onChange={e => setTokenUrl(e.target.value)}/>
                                    </FormField>
                                    <FormField label={i18n.inboundConnectors.sapSfEc.samlIssuer} htmlFor="sap-sf-ec-samlIssuer">
                                        <Input id="sap-sf-ec-samlIssuer" value={samlIssuer}
                                               onChange={e => setSamlIssuer(e.target.value)}/>
                                    </FormField>
                                    <FormField label={i18n.inboundConnectors.sapSfEc.samlAudience} htmlFor="sap-sf-ec-samlAudience">
                                        <Input id="sap-sf-ec-samlAudience" value={samlAudience}
                                               onChange={e => setSamlAudience(e.target.value)}/>
                                    </FormField>
                                    <FormField label={i18n.inboundConnectors.sapSfEc.samlSubject} htmlFor="sap-sf-ec-samlSubject">
                                        <Input id="sap-sf-ec-samlSubject" value={samlSubject}
                                               onChange={e => setSamlSubject(e.target.value)}/>
                                    </FormField>
                                    <FormField label={i18n.inboundConnectors.sapSfEc.samlSigningKeyRef} htmlFor="sap-sf-ec-samlSigningKeyRef">
                                        <SecretReferenceInput id="sap-sf-ec-samlSigningKeyRef"
                                                              value={samlSigningKeyRef}
                                                              onChange={setSamlSigningKeyRef}/>
                                    </FormField>
                                    <FormField label={i18n.inboundConnectors.sapSfEc.samlSigningCertificateRef} htmlFor="sap-sf-ec-samlSigningCertificateRef">
                                        <SecretReferenceInput id="sap-sf-ec-samlSigningCertificateRef"
                                                              value={samlSigningCertificateRef}
                                                              onChange={setSamlSigningCertificateRef}/>
                                    </FormField>
                                    <FormField label={i18n.inboundConnectors.sapSfEc.samlAssertionTemplate}
                                               htmlFor="sap-sf-ec-samlAssertionTemplate" fullWidth>
                                        <Textarea id="sap-sf-ec-samlAssertionTemplate"
                                                  value={samlAssertionTemplate}
                                                  onChange={e => setSamlAssertionTemplate(e.target.value)}
                                                  rows={6}
                                                  className="font-mono text-xs"/>
                                    </FormField>
                                </FormGrid>
                            </>
                        )}
                    </FormSection>

                    <FormSection title={i18n.inboundConnectors.sectionPagination}>
                        <FormGrid columns={2}>
                            <FormField label={i18n.inboundConnectors.pageSize} htmlFor="sap-sf-ec-pageSize">
                                <Input id="sap-sf-ec-pageSize" type="number" value={pageSize}
                                       onChange={e => setPageSize(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.connectTimeout} htmlFor="sap-sf-ec-connectTimeout">
                                <Input id="sap-sf-ec-connectTimeout" type="number" value={connectTimeoutMs}
                                       onChange={e => setConnectTimeoutMs(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.readTimeout} htmlFor="sap-sf-ec-readTimeout">
                                <Input id="sap-sf-ec-readTimeout" type="number" value={readTimeoutMs}
                                       onChange={e => setReadTimeoutMs(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.maxRetries} htmlFor="sap-sf-ec-maxRetries">
                                <Input id="sap-sf-ec-maxRetries" type="number" value={maxRetries}
                                       onChange={e => setMaxRetries(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                    </FormSection>
                </DialogBody>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="btn-cancel-sap-sf-ec-config">{i18n.common.cancel}</Button>
                    <Button onClick={handleSave} disabled={updateMutation.isPending}
                            data-testid="btn-save-sap-sf-ec-config">
                        {updateMutation.isPending && <ButtonSpinner/>}
                        {i18n.common.save}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
